import { useEffect, useState } from "preact/hooks";
import type { Upload } from "@campaign-media/shared";
import { PhotoClient } from "./photo-client.js";
import { MasonryGrid } from "./components/MasonryGrid.js";
import { Lightbox } from "./components/Lightbox.js";
import { ViewToggle } from "./components/ViewToggle.js";

interface Props { client: PhotoClient; }

export function App({ client }: Props) {
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [items, setItems] = useState<Upload[]>([]);
  const [page, setPage] = useState(0);
  const [done, setDone] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [mode, setMode] = useState<"collage" | "browse">("collage");

  useEffect(() => {
    client.fetchActiveCampaignId().then(setCampaignId);
  }, [client]);

  useEffect(() => {
    if (!campaignId) return;
    client.fetchPage(campaignId, page).then((batch) => {
      setItems((prev) => (page === 0 ? batch : [...prev, ...batch]));
      if (batch.length < 24) setDone(true);
    });
  }, [campaignId, page, client]);

  useEffect(() => {
    if (mode === "browse" && items.length > 0) setLightboxIdx(0);
    if (mode === "collage") setLightboxIdx(null);
  }, [mode, items.length]);

  if (!campaignId) return <div class="empty">No campaign in progress.</div>;
  if (items.length === 0) return <div class="empty">Be the first to share!</div>;

  const prev = () => setLightboxIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  const next = () => setLightboxIdx((i) => (i === null ? null : (i + 1) % items.length));

  return (
    <div class="root">
      <ViewToggle mode={mode} onChange={setMode} />
      {mode === "collage" && (
        <MasonryGrid items={items} urlFor={(p) => client.publicUrl(p)} onTileClick={setLightboxIdx} />
      )}
      {!done && mode === "collage" && (
        <button onClick={() => setPage((p) => p + 1)} style="display:block;margin:16px auto;padding:8px 16px">
          Load more
        </button>
      )}
      {lightboxIdx !== null && (
        <Lightbox
          items={items}
          index={lightboxIdx}
          urlFor={(p) => client.publicUrl(p)}
          onClose={() => { setLightboxIdx(null); setMode("collage"); }}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
}
